// Components that render themselves recursively through popups (a compound
// metric form opens a nested MetricPopup; a Studio report opens a popup
// Report; a grid's slide-out opens a PopupGrid) are injected here instead of
// imported, so the import graph has no cycle.
//
// This lives apart from the provider on purpose: consumers import only this
// file, while the provider imports the real components. Don't import any
// component modules here.
import React from 'react';

interface InjectedMetricPopupProps {
  mode: 'Metric' | 'CompoundMetric';
  close: () => void;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  selectedMetric?: Metrics.NormalMetric | Metrics.CompoundMetric;
}

interface InjectedReportProps {
  report: ReportType;
  hideMenu?: boolean;
  parentDashboardGadget?: DashboardGadget;
  unSavedFilter?: UnSavedFilter;
}

// A records grid that moves to DataTable surface by surface (see
// components/Grid/RecordsGrid)
interface InjectedRecordsGridProps {
  surface: DataTable.Surface;
  sizing: DataTable.SizingMode;
  skipHeaderWhenSizing?: boolean;
  isResizable?: boolean;
  layoutOnFirstRender?: boolean;
  layoutOnColumnChange?: boolean;
  layoutOnModelUpdated?: boolean;
  hasBorders: boolean;
  hasDarkHeader: boolean;
}

interface InjectedComponentsContextType {
  RecordsGrid: React.ComponentType<InjectedRecordsGridProps>;
  MetricPopup: React.ComponentType<InjectedMetricPopupProps>;
  Report: React.ComponentType<InjectedReportProps>;
}

const missingComponent = (name: string) => () => {
  const error = new Error();
  error.name = `InjectedComponents: ${name} is not provided`;
  throw error;
};

export const InjectedComponentsContext =
  React.createContext<InjectedComponentsContextType>({
    RecordsGrid: missingComponent('RecordsGrid'),
    MetricPopup: missingComponent('MetricPopup'),
    Report: missingComponent('Report'),
  });
